/**
 * 選手とパラグライダーの 3D モデル（docs/05_rendering-and-audio.md 8 章）。すべてコードで作る。
 *
 * 時間は ×330 前後で流れるため、ゲームの時間どおりに動かすと脚の振りや旋回が速すぎて見えない。
 * 歩く脚の振り・サーマルでの旋回は「見た目の動き」として現実の時間で動かし、位置と向きだけ sim に合わせる。
 * 遠くからでも姿勢と向きが分かるよう、カメラから離れるほど少し大きく描く（上限あり）。
 */
import * as THREE from 'three';
import type { NodeData } from '../../core/world';
import { dirToAzimuth } from '../../core/vec';
import type { AthleteState } from '../../sim/types';

/** キャノピーの寸法（m） */
const LINE_LEN = 7.6;
const CHORD = 2.6;
/** 翼端までの弧の角度（アンヘドラル） */
const ARC = THREE.MathUtils.degToRad(42);
const SPAN_SEG = 32;
const FOIL_SEG = 12;

/** 旋回中の見た目の回転（1 秒あたりの回転数）と傾き */
const CIRCLE_REV_PER_S = 0.35;
const CIRCLE_BANK = THREE.MathUtils.degToRad(28);

/** 見た目の大きさの補正：この距離より遠いと拡大する（歩き・飛行）、倍率の上限、描く距離 */
const REF_DIST_WALK = 35;
const REF_DIST_FLY = 170;
const MAX_SCALE = 12;
const MAX_DRAW_DIST = 6000;

interface Person {
  root: THREE.Group;
  legL: THREE.Group;
  legR: THREE.Group;
  armL: THREE.Group;
  armR: THREE.Group;
  pack: THREE.Mesh;
}

interface Item {
  root: THREE.Group;
  /** ロール（傾き）をかける層 */
  rig: THREE.Group;
  person: Person;
  glider: THREE.Group;
  /** 離陸準備中に地面に広げたグライダー */
  spread: THREE.Group;
  tent: THREE.Mesh;
  heading: number;
  bank: number;
  /** 表示位置（飛行中は sim の位置をならす） */
  shown: THREE.Vector3;
  last: THREE.Vector3;
  circlePhase: number;
  init: boolean;
  /** 止まっている選手どうしを横に並べるずらし（なめらかに入れる） */
  offset: THREE.Vector3;
  /** 飛行中の位置の履歴（現実の時刻つき）。旋回を中心にならし、まっすぐ飛ぶときは遅れないように使う */
  hist: { x: number; y: number; z: number; t: number }[];
  /** 飛行 ⇔ 地上が切り替わった直後に、表示位置をなめらかにつなぐ残り時間（現実の秒） */
  blendS: number;
  wasFlying: boolean;
}

/** 飛行中の位置をならす時間の幅（現実の秒） */
const FLY_WINDOW_S = 0.3;

export interface ModelPose {
  /** 表示位置（カメラが追う点） */
  pos: THREE.Vector3;
  /** 表示している向き（方位、ラジアン） */
  heading: number;
  /** 見た目の拡大率（カメラが注視点の高さを合わせるのに使う） */
  scale: number;
}

export class AthleteModels {
  readonly group = new THREE.Group();
  private readonly items = new Map<string, Item>();
  private readonly tmp = new THREE.Vector3();

  constructor(
    athletes: AthleteState[],
    private readonly groundAt: (x: number, z: number) => number,
    private readonly nodes: () => readonly NodeData[],
  ) {
    this.group.name = 'athletes';
    for (const a of athletes) {
      const color = new THREE.Color(a.color);
      const root = new THREE.Group();
      root.rotation.order = 'YXZ';
      const rig = new THREE.Group();
      rig.rotation.order = 'YXZ';
      const person = buildPerson(color);
      const glider = buildGlider(color);
      const spread = buildSpread(color);
      const tent = buildTent(color);
      rig.add(person.root, glider, spread);
      root.add(rig, tent);
      this.group.add(root);
      this.items.set(a.id, {
        root,
        rig,
        person,
        glider,
        spread,
        tent,
        heading: 0,
        bank: 0,
        shown: new THREE.Vector3(a.pos.x, a.pos.y, a.pos.z),
        last: new THREE.Vector3(a.pos.x, a.pos.y, a.pos.z),
        circlePhase: 0,
        init: false,
        offset: new THREE.Vector3(),
        hist: [],
        blendS: 0,
        wasFlying: false,
      });
    }
  }

  /** 表示位置と向き（追従カメラが使う） */
  pose(id: string): ModelPose | null {
    const it = this.items.get(id);
    return it ? { pos: it.shown, heading: it.heading, scale: it.root.scale.x } : null;
  }

  update(athletes: AthleteState[], camera: THREE.Camera, nowMs: number, realDt: number): void {
    const t = nowMs / 1000;
    const still = (a: AthleteState): boolean => a.mode !== 'flying' && a.mode !== 'hiking';
    athletes.forEach((a, index) => {
      const it = this.items.get(a.id);
      if (!it) return;
      const f = a.flight;
      const flying = a.mode === 'flying' && f !== null;
      // 旋回（サーマル・着陸地点の上）は、機体の傾きが続いていることで見分ける
      const circling = flying && f.circlingS > 1;

      // 同じ場所（離陸地・CP・ビバーク）に止まっている選手は、重ならないよう横に並べる（ずらしはなめらかに）
      const want = new THREE.Vector3();
      const hiking = a.mode === 'hiking';
      if (
        (still(a) &&
          athletes.some(
            (o) => o !== a && still(o) && Math.hypot(o.pos.x - a.pos.x, o.pos.z - a.pos.z) < 20,
          )) ||
        // 同じ山道をほぼ同じ位置で歩いている選手も、道幅の中で少し横に並べる
        (hiking &&
          athletes.some(
            (o) => o !== a && o.mode === 'hiking' && Math.hypot(o.pos.x - a.pos.x, o.pos.z - a.pos.z) < 6,
          ))
      ) {
        const gap = a.mode === 'preparing' || a.mode === 'waitingWind' ? 16 : hiking ? 1.6 : 3;
        const side = (index - (athletes.length - 1) / 2) * gap;
        want.set(Math.cos(it.heading) * side, 0, Math.sin(it.heading) * side);
      }
      it.offset.lerp(want, 1 - Math.exp(-realDt / 0.15));

      // 位置：飛行中は直近の位置の平均に、進む向きの補正を足す（旋回は中心に落ち着き、まっすぐ飛ぶときは遅れない）
      const target = this.tmp.set(a.pos.x, a.pos.y, a.pos.z);
      if (flying) {
        it.hist.push({ x: a.pos.x, y: a.pos.y, z: a.pos.z, t });
        while (it.hist.length > 2 && t - (it.hist[0] as { t: number }).t > FLY_WINDOW_S) it.hist.shift();
        if (it.hist.length >= 3) fitEnd(it.hist, target);
      } else {
        it.hist.length = 0;
        target.add(it.offset);
        target.y = this.groundAt(target.x, target.z);
      }
      if (flying !== it.wasFlying) it.blendS = 0.4;
      it.wasFlying = flying;
      if (!it.init) {
        it.shown.copy(target);
        it.last.copy(target);
        it.init = true;
      } else if (it.blendS > 0) {
        // 離陸・着陸の直後は、表示位置の求め方が変わるので短くなめらかにつなぐ
        it.blendS -= realDt;
        it.shown.lerp(target, 1 - Math.exp(-realDt / 0.08));
      } else {
        it.shown.copy(target);
      }

      // 向き
      if (flying) {
        if (circling) {
          const dir = f.ap.circleDir;
          it.circlePhase += dir * CIRCLE_REV_PER_S * Math.PI * 2 * realDt;
          it.heading = it.circlePhase;
          it.bank = approach(it.bank, dir * CIRCLE_BANK, realDt, 0.4);
        } else {
          it.heading = approachAngle(it.heading, f.headingRad, realDt, 0.25);
          it.circlePhase = it.heading;
          it.bank = approach(it.bank, THREE.MathUtils.clamp(f.bankRad, -0.8, 0.8), realDt, 0.3);
        }
      } else {
        const dx = target.x - it.last.x;
        const dz = target.z - it.last.z;
        if (a.mode === 'hiking' && Math.hypot(dx, dz) > 0.5) {
          it.heading = approachAngle(it.heading, dirToAzimuth(dx, dz), realDt, 0.2);
        } else if ((a.mode === 'preparing' || a.mode === 'waitingWind') && a.nodeId !== null) {
          const n = this.nodes()[a.nodeId];
          if (n?.launch) it.heading = approachAngle(it.heading, n.launch.facingRad, realDt, 0.3);
        }
        it.circlePhase = it.heading;
        it.bank = approach(it.bank, 0, realDt, 0.3);
      }
      it.last.copy(target);

      it.root.position.copy(it.shown);
      it.root.rotation.y = -it.heading;
      it.rig.rotation.z = -it.bank;

      // 見た目の大きさと、遠すぎるときは描かない
      const d = camera.position.distanceTo(it.shown);
      it.root.visible = d < MAX_DRAW_DIST;
      const scale = THREE.MathUtils.clamp(d / (flying ? REF_DIST_FLY : REF_DIST_WALK), 1, MAX_SCALE);
      it.root.scale.setScalar(scale);

      // 姿勢（準備中は、背後の斜面の傾きに合わせてグライダーを広げる）
      let slope = 0;
      if (a.mode === 'preparing' || a.mode === 'waitingWind') {
        const back = SPREAD_DIST * scale;
        const bx = it.shown.x - Math.sin(it.heading) * back;
        const bz = it.shown.z + Math.cos(it.heading) * back;
        slope = Math.atan2(this.groundAt(bx, bz) - it.shown.y, back);
      }
      pose(it, a, t, flying, slope);
    });
  }
}

function pose(it: Item, a: AthleteState, t: number, flying: boolean, slope: number): void {
  const p = it.person;
  const glider = it.glider;
  const preparing = a.mode === 'preparing' || a.mode === 'waitingWind';
  it.spread.visible = preparing;
  it.spread.rotation.x = -slope;
  it.tent.visible = a.mode === 'bivouac';
  p.root.visible = a.mode !== 'bivouac';
  p.pack.scale.setScalar(a.mode === 'packing' ? 1.4 : 1);

  if (flying) {
    // ハーネスに座った姿：腰を原点に、脚を前へ、腕はブレークを持って上へ
    glider.visible = true;
    p.root.position.set(0, -0.9, 0);
    p.root.rotation.set(-0.15, 0, 0);
    p.legL.rotation.x = p.legR.rotation.x = 1.35;
    p.armL.rotation.x = p.armR.rotation.x = 2.7;
    p.armL.rotation.z = -0.25;
    p.armR.rotation.z = 0.25;
    return;
  }

  p.root.position.set(0, 0, 0);
  p.root.rotation.set(0, 0, 0);
  p.armL.rotation.z = p.armR.rotation.z = 0;
  glider.visible = false;
  if (preparing) {
    // 背後に広げたグライダーのライザーを持って、谷を向いて立つ
    p.legL.rotation.x = p.legR.rotation.x = 0;
    p.armL.rotation.x = p.armR.rotation.x = 0.5;
    return;
  }

  if (a.mode === 'hiking') {
    const ph = t * Math.PI * 2 * 1.5;
    const s = Math.sin(ph);
    p.legL.rotation.x = s * 0.55;
    p.legR.rotation.x = -s * 0.55;
    p.armL.rotation.x = -s * 0.4;
    p.armR.rotation.x = s * 0.4;
    p.root.position.y = Math.abs(Math.cos(ph)) * 0.06;
    p.root.rotation.x = -0.12;
  } else {
    p.legL.rotation.x = p.legR.rotation.x = 0;
    p.armL.rotation.x = p.armR.rotation.x = 0;
  }
}

/** 位置の履歴に直線を当てはめ、最新の時刻での位置を out に入れる（平均 ＋ 平均の速度 × 時刻の差） */
function fitEnd(hist: { x: number; y: number; z: number; t: number }[], out: THREE.Vector3): void {
  const n = hist.length;
  let mt = 0;
  let mx = 0;
  let my = 0;
  let mz = 0;
  for (const h of hist) {
    mt += h.t;
    mx += h.x;
    my += h.y;
    mz += h.z;
  }
  mt /= n;
  mx /= n;
  my /= n;
  mz /= n;
  let stt = 0;
  let stx = 0;
  let sty = 0;
  let stz = 0;
  for (const h of hist) {
    const dt = h.t - mt;
    stt += dt * dt;
    stx += dt * (h.x - mx);
    sty += dt * (h.y - my);
    stz += dt * (h.z - mz);
  }
  const last = hist[n - 1] as { t: number };
  const k = stt > 1e-9 ? (last.t - mt) / stt : 0;
  out.set(mx + stx * k, my + sty * k, mz + stz * k);
}

function approach(v: number, target: number, dt: number, tau: number): number {
  return v + (target - v) * (1 - Math.exp(-dt / tau));
}

function approachAngle(v: number, target: number, dt: number, tau: number): number {
  let d = target - v;
  d = Math.atan2(Math.sin(d), Math.cos(d));
  return v + d * (1 - Math.exp(-dt / tau));
}

// ---- 形 ----

function box(w: number, h: number, d: number, color: THREE.ColorRepresentation): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    new THREE.MeshStandardMaterial({ color, roughness: 0.8 }),
  );
}

/** 足元を原点に、前が −z の人。脚と腕は付け根で回せるようにグループに入れる */
function buildPerson(color: THREE.Color): Person {
  const root = new THREE.Group();
  const pants = 0x2b2f36;
  const limb = (w: number, len: number, c: THREE.ColorRepresentation, x: number, y: number): THREE.Group => {
    const g = new THREE.Group();
    g.position.set(x, y, 0);
    const m = box(w, len, w, c);
    m.position.y = -len / 2;
    g.add(m);
    root.add(g);
    return g;
  };
  const legL = limb(0.15, 0.88, pants, -0.11, 0.9);
  const legR = limb(0.15, 0.88, pants, 0.11, 0.9);
  const armL = limb(0.1, 0.62, color, -0.28, 1.46);
  const armR = limb(0.1, 0.62, color, 0.28, 1.46);
  const torso = box(0.44, 0.62, 0.26, color);
  torso.position.y = 1.2;
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.13, 12, 8),
    new THREE.MeshStandardMaterial({ color: 0xf4f4f4, roughness: 0.4 }),
  );
  head.position.y = 1.64;
  const pack = box(0.42, 0.66, 0.34, color.clone().multiplyScalar(0.75));
  pack.position.set(0, 1.22, 0.3);
  root.add(torso, head, pack);
  return { root, legL, legR, armL, armR, pack };
}

/** セル i の色：翼端と中央に白、ところどころ濃い色（遠くからでも選手の色と模様で見分けられるように） */
function cellColor(i: number, color: THREE.Color): THREE.Color {
  const cell = Math.min(i, SPAN_SEG - 1);
  if (cell < 2 || cell >= SPAN_SEG - 2 || cell % 8 === 3 || cell % 8 === 4) return WHITE;
  return cell % 8 === 0 ? color.clone().multiplyScalar(0.55) : color;
}
const WHITE = new THREE.Color(0xf6f6f2);

/** 広げたグライダーの前縁までの距離（パイロットの足元から） */
const SPREAD_DIST = LINE_LEN - 1.2;

/** 離陸準備中：パイロットの背後の地面に、前縁をパイロットに向けて三日月形に広げたグライダー */
function buildSpread(color: THREE.Color): THREE.Group {
  const g = new THREE.Group();
  const pos: number[] = [];
  const col: number[] = [];
  const idx: number[] = [];
  const rows = 4;
  for (let i = 0; i <= SPAN_SEG; i++) {
    const s = (i / SPAN_SEG) * 2 - 1;
    const a = s * ARC * 1.25;
    const chord = CHORD * Math.sqrt(1 - 0.8 * s * s);
    const c = cellColor(i, color);
    for (let k = 0; k <= rows; k++) {
      const r = SPREAD_DIST + (k / rows) * chord;
      pos.push(Math.sin(a) * r, 0.08 + 0.12 * Math.sin((k / rows) * Math.PI), Math.cos(a) * r);
      col.push(c.r, c.g, c.b);
    }
  }
  for (let i = 0; i < SPAN_SEG; i++) {
    for (let k = 0; k < rows; k++) {
      const a = i * (rows + 1) + k;
      const b = a + rows + 1;
      idx.push(a, a + 1, b, a + 1, b + 1, b);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  g.add(
    new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({ vertexColors: true, side: THREE.DoubleSide, roughness: 0.55 }),
    ),
  );
  // ライン：前縁からパイロットの肩へ
  const lines: number[] = [];
  for (const s of [-0.9, -0.6, -0.3, 0.3, 0.6, 0.9]) {
    const a = s * ARC * 1.25;
    const riserX = s < 0 ? -0.22 : 0.22;
    for (const dr of [0.1, 0.8]) {
      const r = SPREAD_DIST + dr;
      lines.push(Math.sin(a) * r, 0.1, Math.cos(a) * r, riserX, 1.35, 0.1);
    }
  }
  const lineGeo = new THREE.BufferGeometry();
  lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(lines, 3));
  g.add(
    new THREE.LineSegments(
      lineGeo,
      new THREE.LineBasicMaterial({ color: 0x2a2a2a, transparent: true, opacity: 0.55 }),
    ),
  );
  g.visible = false;
  return g;
}

/** パイロットの腰を原点に、上にキャノピー、下へラインが伸びるグライダー */
function buildGlider(color: THREE.Color): THREE.Group {
  const g = new THREE.Group();

  // 翼の表面：スパン方向 i、翼型を一周する方向 k（後縁 → 上面 → 前縁 → 下面 → 後縁）
  const cols = FOIL_SEG * 2 + 1;
  const pos: number[] = [];
  const col: number[] = [];
  const idx: number[] = [];
  const station = (s: number): { a: number; chord: number } => ({
    a: s * ARC,
    chord: CHORD * Math.sqrt(1 - 0.8 * s * s),
  });
  for (let i = 0; i <= SPAN_SEG; i++) {
    const s = (i / SPAN_SEG) * 2 - 1;
    const { a, chord } = station(s);
    const rx = Math.sin(a);
    const ry = Math.cos(a);
    const c = cellColor(i, color);
    for (let k = 0; k < cols; k++) {
      const top = k <= FOIL_SEG;
      const tt = top ? 1 - k / FOIL_SEG : (k - FOIL_SEG) / FOIL_SEG;
      const thick = 0.14 * chord * Math.sqrt(Math.max(tt, 0)) * (1 - tt);
      const off = top ? thick * 0.85 : -thick * 0.25;
      const r = LINE_LEN + off;
      // 前縁（tt=0）が前（−z）
      pos.push(rx * r, ry * r, -0.35 * chord + tt * chord);
      col.push(c.r, c.g, c.b);
    }
  }
  for (let i = 0; i < SPAN_SEG; i++) {
    for (let k = 0; k < cols - 1; k++) {
      const a = i * cols + k;
      const b = a + cols;
      idx.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  const canopy = new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({ vertexColors: true, side: THREE.DoubleSide, roughness: 0.55 }),
  );
  g.add(canopy);

  // ライン：下面の A・B・C 列からライザー（肩）へ
  const lines: number[] = [];
  for (const s of [-0.95, -0.7, -0.45, -0.2, 0.2, 0.45, 0.7, 0.95]) {
    const { a, chord } = station(s);
    const riserX = s < 0 ? -0.22 : 0.22;
    for (const tt of [0.1, 0.35, 0.65]) {
      const r = LINE_LEN - 0.05;
      lines.push(Math.sin(a) * r, Math.cos(a) * r, -0.35 * chord + tt * chord, riserX, 0.55, 0.05);
    }
  }
  const lineGeo = new THREE.BufferGeometry();
  lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(lines, 3));
  g.add(
    new THREE.LineSegments(
      lineGeo,
      new THREE.LineBasicMaterial({ color: 0x2a2a2a, transparent: true, opacity: 0.55 }),
    ),
  );
  return g;
}

/** 小さな三角テント（ビバーク） */
function buildTent(color: THREE.Color): THREE.Mesh {
  const shape = new THREE.Shape();
  shape.moveTo(-0.7, 0);
  shape.lineTo(0.7, 0);
  shape.lineTo(0, 1.0);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: 2.2, bevelEnabled: false });
  geo.translate(0, 0, -1.1);
  const tent = new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({
      color: color.clone().lerp(new THREE.Color(0xffffff), 0.2),
      roughness: 0.7,
    }),
  );
  tent.position.set(1.2, 0, 0);
  tent.visible = false;
  return tent;
}
